import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, X, Check, RotateCcw, Image as ImageIcon, Aperture } from 'lucide-react';
import { toast } from 'sonner';

// Auto-crop a captured photo to the receipt: trim near-white/near-dark
// margins around the edges so the stored image is a tight crop of the
// receipt — 100% on-device (no network / LLM cost).
const autoCropReceipt = (dataUrl) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = img.width;
    c.height = img.height;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0);

    // Downsample for detection speed
    const scale = Math.min(1, 400 / Math.max(img.width, img.height));
    const dw = Math.max(1, Math.round(img.width * scale));
    const dh = Math.max(1, Math.round(img.height * scale));
    const dc = document.createElement('canvas');
    dc.width = dw; dc.height = dh;
    const dctx = dc.getContext('2d');
    dctx.drawImage(img, 0, 0, dw, dh);
    const data = dctx.getImageData(0, 0, dw, dh).data;

    // A "content" pixel is any pixel far enough from pure background
    // (either very light or very dark). Receipts are usually bright white
    // on a darker surface OR dark ink on white paper — both trip this.
    const isContent = (i) => {
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      return lum < 235 && lum > 20; // ignore washed-white and pitch-black
    };

    let minX = dw, minY = dh, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < dh; y++) {
      for (let x = 0; x < dw; x++) {
        if (isContent((y * dw + x) * 4)) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }

    if (!found || maxX - minX < dw * 0.3 || maxY - minY < dh * 0.3) {
      // Couldn't confidently find a subject — return original untouched.
      resolve(dataUrl);
      return;
    }

    // Map bounds back to the full-res image + add 4% padding
    const pad = 0.04;
    const invScale = 1 / scale;
    const x0 = Math.max(0, Math.floor((minX - dw * pad) * invScale));
    const y0 = Math.max(0, Math.floor((minY - dh * pad) * invScale));
    const x1 = Math.min(img.width, Math.ceil((maxX + dw * pad) * invScale));
    const y1 = Math.min(img.height, Math.ceil((maxY + dh * pad) * invScale));

    const out = document.createElement('canvas');
    out.width = x1 - x0;
    out.height = y1 - y0;
    out.getContext('2d').drawImage(img, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
    resolve(out.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = () => resolve(dataUrl);
  img.src = dataUrl;
});

const ReceiptCamera = ({ onCapture, existingImage }) => {
  const [capturedImage, setCapturedImage] = useState(existingImage || null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [cameraError, setCameraError] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start camera stream
  const startCamera = useCallback(async () => {
    setCameraError(null);
    
    try {
      // Stop any existing stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.message || 'Could not access camera');
      
      if (err.name === 'NotAllowedError') {
        toast.error('Camera permission denied. Please allow camera access.');
      } else if (err.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Could not access camera. Try uploading an image instead.');
      }
    }
  }, [facingMode, cameraStream]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  // Switch between front and back camera
  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  // Effect to start/stop camera when modal opens/closes
  useEffect(() => {
    if (showCameraModal && !previewImage) {
      startCamera();
    }
    
    return () => {
      if (!showCameraModal) {
        stopCamera();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCameraModal, facingMode]);

  // Capture photo from video stream
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get image data as JPEG with compression
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setPreviewImage(imageData);
    
    // Stop camera stream after capture
    stopCamera();
  };

  // Retake photo
  const retakePhoto = () => {
    setPreviewImage(null);
    startCamera();
  };

  // Confirm and save photo (auto-crop on-device, no AI call)
  const confirmPhoto = async () => {
    if (!previewImage) return;
    const cropped = await autoCropReceipt(previewImage);
    setCapturedImage(cropped);
    onCapture(cropped);
    closeCameraModal();
    toast.success('Receipt captured!');
  };

  // Close camera modal
  const closeCameraModal = () => {
    stopCamera();
    setPreviewImage(null);
    setCameraError(null);
    setShowCameraModal(false);
  };

  // Open camera modal
  const openCamera = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setShowCameraModal(true);
    } else {
      toast.error('Camera not supported. Please upload an image.');
      fileInputRef.current?.click();
    }
  };

  // Handle file upload (fallback)
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      const compressedImage = await compressImage(file);
      const cropped = await autoCropReceipt(compressedImage);
      setCapturedImage(cropped);
      onCapture(cropped);
      toast.success('Receipt uploaded!');
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image');
    }
    
    e.target.value = '';
  };

  // Compress image utility
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Remove captured image
  const handleRemove = () => {
    setCapturedImage(null);
    onCapture(null);
    toast.info('Receipt removed');
  };

  return (
    <>
      {/* Hidden file input for fallback */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Capture Buttons (when no image) */}
      {!capturedImage && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {/* Camera Button */}
          <button
            type="button"
            onClick={openCamera}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '1rem',
              border: '2px dashed #0073ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#0073ea',
              background: '#f8f9ff',
              transition: 'all 0.2s',
              textAlign: 'center',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#eef0ff';
              e.currentTarget.style.borderColor = '#5568d3';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f8f9ff';
              e.currentTarget.style.borderColor = '#0073ea';
            }}
          >
            <Camera size={24} />
            <span>Take Photo</span>
          </button>

          {/* Upload Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '1rem',
              border: '2px dashed #0073ea',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#0073ea',
              background: '#f8f9ff',
              transition: 'all 0.2s',
              textAlign: 'center',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#eef0ff';
              e.currentTarget.style.borderColor = '#5568d3';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f8f9ff';
              e.currentTarget.style.borderColor = '#0073ea';
            }}
          >
            <ImageIcon size={24} />
            <span>Upload Image</span>
          </button>
        </div>
      )}

      {/* Captured Image Preview */}
      {capturedImage && (
        <div style={{
          position: 'relative',
          border: '2px solid #10b981',
          borderRadius: '8px',
          padding: '0.75rem',
          background: '#f0fdf4'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img
              src={capturedImage}
              alt="Receipt"
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                cursor: 'pointer'
              }}
              onClick={() => window.open(capturedImage, '_blank')}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#059669', marginBottom: '0.25rem' }}>
                ✓ Receipt Captured
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Click image to view full size
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={openCamera}
                style={{
                  padding: '0.5rem',
                  background: '#0073ea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Retake Photo"
              >
                <Camera size={16} />
              </button>
              <button
                type="button"
                onClick={handleRemove}
                style={{
                  padding: '0.5rem',
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Remove"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      {showCameraModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          {/* Header */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)'
          }}>
            <h3 style={{ color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <Camera size={20} /> Capture Receipt
            </h3>
            <button
              onClick={closeCameraModal}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Camera Preview / Captured Image */}
          <div style={{
            width: '100%',
            maxWidth: '640px',
            aspectRatio: '4/3',
            borderRadius: '16px',
            overflow: 'hidden',
            background: '#000',
            position: 'relative'
          }}>
            {!previewImage ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: cameraStream ? 'block' : 'none'
                  }}
                />
                
                {/* Loading / Error State */}
                {!cameraStream && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    {cameraError ? (
                      <>
                        <X size={48} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                        <p style={{ marginBottom: '16px' }}>{cameraError}</p>
                        <button
                          onClick={() => {
                            closeCameraModal();
                            fileInputRef.current?.click();
                          }}
                          style={{
                            padding: '10px 20px',
                            background: '#0073ea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          Upload Instead
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          border: '3px solid rgba(255,255,255,0.3)',
                          borderTopColor: 'white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          margin: '0 auto 12px'
                        }} />
                        <p>Starting camera...</p>
                      </>
                    )}
                  </div>
                )}

                {/* Receipt Guide Overlay */}
                {cameraStream && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    height: '70%',
                    border: '2px dashed rgba(255,255,255,0.5)',
                    borderRadius: '8px',
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      position: 'absolute',
                      bottom: '-30px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '12px',
                      whiteSpace: 'nowrap'
                    }}>
                      Align receipt within frame
                    </div>
                  </div>
                )}
              </>
            ) : (
              <img
                src={previewImage}
                alt="Captured Receipt"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            )}
          </div>

          {/* Camera Controls */}
          <div style={{
            marginTop: '24px',
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
            {!previewImage ? (
              <>
                {/* Switch Camera */}
                <button
                  onClick={switchCamera}
                  disabled={!cameraStream}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: cameraStream ? 'pointer' : 'not-allowed',
                    color: 'white',
                    opacity: cameraStream ? 1 : 0.5
                  }}
                  title="Switch Camera"
                >
                  <RotateCcw size={24} />
                </button>

                {/* Capture Button */}
                <button
                  onClick={capturePhoto}
                  disabled={!cameraStream}
                  style={{
                    background: 'white',
                    border: '4px solid rgba(255,255,255,0.5)',
                    borderRadius: '50%',
                    width: '72px',
                    height: '72px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: cameraStream ? 'pointer' : 'not-allowed',
                    opacity: cameraStream ? 1 : 0.5
                  }}
                  title="Capture"
                >
                  <Aperture size={32} color="#0073ea" />
                </button>

                {/* Upload from Gallery */}
                <button
                  onClick={() => {
                    closeCameraModal();
                    fileInputRef.current?.click();
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                  title="Upload from Gallery"
                >
                  <ImageIcon size={24} />
                </button>
              </>
            ) : (
              <>
                {/* Retake */}
                <button
                  onClick={retakePhoto}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px 28px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                >
                  <RotateCcw size={20} /> Retake
                </button>

                {/* Confirm */}
                <button
                  onClick={confirmPhoto}
                  style={{
                    background: '#22c55e',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px 28px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '16px'
                  }}
                >
                  <Check size={20} /> Use Photo
                </button>
              </>
            )}
          </div>

          {/* Instructions */}
          <p style={{
            color: 'rgba(255,255,255,0.6)',
            marginTop: '16px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {!previewImage 
              ? 'Position the receipt within the frame and tap capture'
              : 'Review your photo and tap "Use Photo" to save'}
          </p>
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ReceiptCamera;
